import {
  FunctionComponent,
  useCallback,
  useMemo,
  PropsWithChildren,
  createContext,
} from 'react'

import useDispatch from 'hooks/useDispatch'
import {
  AdventureCardFragmentFragmentDoc,
  GetBookingAdventuresQueryResult,
  GetBookingAdventuresDocument,
  useAddAdventureToBookingMutation,
  useGetBookingAdventuresQuery,
  useRemoveAdventureFromBookingMutation,
} from '../../../../hooks/useQueries'

import { sendNotification } from '../../../../actions/notificationActions'

export const BookingItineraryContext = createContext<{
  bookingAdventures: GetBookingAdventuresQueryResult['data']['booking']['adventures']
  handleAddAdventureToBooking: (adventureId: string) => any
  handleRemoveAdventureFromBooking: (adventureId: string) => any
}>({
  bookingAdventures: undefined,
  handleAddAdventureToBooking: undefined,
  handleRemoveAdventureFromBooking: undefined,
})

interface BookingItineraryProviderProps {
  bookingId: string
  onUpdate?: () => any
  analyticsSource: string
}

const BookingItineraryProvider: FunctionComponent<PropsWithChildren<
  BookingItineraryProviderProps
>> = ({ bookingId, onUpdate, children, analyticsSource: source }) => {
  const dispatch = useDispatch()
  const { data } = useGetBookingAdventuresQuery({
    variables: { bookingId },
  })
  const bookingAdventures = data?.booking?.adventures

  const [addAdventureToBooking] = useAddAdventureToBookingMutation()
  const handleAddAdventureToBooking = useCallback(
    (adventureId: string) =>
      addAdventureToBooking({
        variables: {
          bookingId,
          adventureId,
          source,
        },
        optimisticResponse: {
          __typename: 'Mutation',
          createBookingAdventure: {
            __typename: 'CreateBookingAdventurePayload',
            adventure: {
              __typename: 'StoryAdventure',
              id: adventureId,
            },
          },
        },
        update(cache, mutationRes) {
          const addedAdventureId =
            mutationRes.data.createBookingAdventure.adventure.id

          const adventureFragment = cache.readFragment<
            GQL_AdventureCardFragmentFragment
          >({
            fragment: AdventureCardFragmentFragmentDoc,
            id: `StoryAdventure:${addedAdventureId}`,
          })

          // Using writeFragment instead of readFragment because the former returns a reference to the cache object,
          // rather than the object itself. The field we are updating in cache contains references.
          const adventureFragmentRef = cache.writeFragment({
            fragment: AdventureCardFragmentFragmentDoc,
            id: `StoryAdventure:${addedAdventureId}`,
            data: adventureFragment,
          })

          cache.modify({
            id: cache.identify({
              __typename: 'Booking',
              id: bookingId,
            }),
            fields: {
              adventures(cachedAdventures = []) {
                return [...cachedAdventures, adventureFragmentRef]
              },
            },
          })
        },
      }).then(() => {
        if (onUpdate) onUpdate()
        dispatch(sendNotification(`Added to your itinerary!`))
      }),
    [bookingId, bookingAdventures, onUpdate, addAdventureToBooking]
  )

  const [removeAdventureFromBooking] = useRemoveAdventureFromBookingMutation()

  const handleRemoveAdventureFromBooking = useCallback(
    (adventureId: string) =>
      removeAdventureFromBooking({
        variables: {
          bookingId,
          adventureId,
        },
        optimisticResponse: {
          __typename: 'Mutation',
          destroyBookingAdventure: {
            __typename: 'DestroyBookingAdventurePayload',
            adventures: bookingAdventures.filter(
              (adventure) => adventure.id !== adventureId
            ),
          },
        },
        update(cache) {
          cache.writeQuery({
            query: GetBookingAdventuresDocument,
            data: {
              booking: {
                __typename: 'Booking',
                id: bookingId,
                adventures: bookingAdventures.filter(
                  (adventure) => adventure.id !== adventureId
                ),
              },
            },
          })
        },
      }).then(() => {
        if (onUpdate) onUpdate()
        dispatch(sendNotification(`Removed from your itinerary!`))
      }),
    [bookingId, bookingAdventures, onUpdate, removeAdventureFromBooking]
  )

  const value = useMemo(
    () => ({
      bookingAdventures,
      handleAddAdventureToBooking,
      handleRemoveAdventureFromBooking,
    }),
    [
      bookingAdventures,
      handleAddAdventureToBooking,
      handleRemoveAdventureFromBooking,
    ]
  )

  return (
    <BookingItineraryContext.Provider value={value}>
      {children}
    </BookingItineraryContext.Provider>
  )
}

export const BookingItineraryConsumer = BookingItineraryContext.Consumer
export default BookingItineraryProvider
